import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Command, Server, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CapabilitiesProps } from '@/types';

/**
 * Capabilities Section Component
 * Displays what users can do with NeurosEdge
 */
export function Capabilities({ data, className }: CapabilitiesProps) {
  return (
    <section id="capabilities" className={cn("py-20 bg-background", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gradient">
            {data.title}
          </h2>
        </div>

        {/* Capabilities Grid */}
        <div className="max-w-4xl mx-auto">
          {/* Command Examples */}
          <div className="grid gap-6 mb-12">
            {data.items.map((item, index) => (
              <Card key={index} className="gradient-card border-border/10 shadow-card">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        {index === 0 && <Server className="w-6 h-6 text-primary" />}
                        {index === 1 && <Shield className="w-6 h-6 text-primary" />}
                        {index === 2 && <CheckCircle className="w-6 h-6 text-primary" />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-medium text-foreground">
                        {item}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer Message */}
          <div className="text-center">
            <Card className="gradient-card border-primary/20 shadow-glow max-w-2xl mx-auto">
              <CardContent className="p-8">
                <p className="text-xl font-semibold text-primary mb-4">
                  {data.footer}
                </p>
                <div className="flex items-center justify-center space-x-8 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>No Tickets</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>No Code Generation</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Secure Execution</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
